import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI */
import { Box, Tooltip, IconButton } from '@mui/material';

/** Material UI Icons */
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

/** Internal Components */
import { callAPI } from 'shared/api/callApi';
import { SelectField_single_None } from 'shared/ui/SelectForm';
import ThemeButton from 'shared/ui/Button';
import { MainDatagrid } from 'shared/ui/Datagrid';
import { AutocompleteField_Single } from 'shared/ui/AutoCompleteForm';
import { CustomSkeleton2 } from 'shared/ui/Skeleton';

import { getEntryColumns, getTruckingColumns } from './JinroERPDef';
import JinroERPDetail from './JinroERPDetail';

export default function JinroERP({ getAccessTokenSilently, userEmail, loadingState, setLoadingState }) {
    const [jinroSummariesRows, setJinroSummariesRows] = useState([]);
    const [entryWeekList, setEntryWeekList] = useState([]);
    const [truckingWeekList, setTruckingWeekList] = useState([]);
    const [type, setType] = useState('ENTRY');
    const [week, setWeek] = useState(null);
    const [columnsType, setColumnsType] = useState('ENTRY');

    const weekOptions = type === 'ENTRY' ? entryWeekList : truckingWeekList;

    const handleTypeChange = (event) => {
        const nextType = event.target.value;
        setType(nextType);
        const list = nextType === 'ENTRY' ? entryWeekList : truckingWeekList;
        if (list.length && (week == null || !list.includes(week))) {
            setWeek(list[list.length - 1]);
        }
    };

    const handleSearchClick = () => {
        setColumnsType(type);
        callAPI({
            method: 'get',
            module: 'duty_calculator',
            component: 'jinro_summaries',
            type: type.toLowerCase(),
            condition: week,
            getAccessTokenSilently,
            email: userEmail,
            onInit: () => {
                setLoadingState((prev) => ({ ...prev, jinroSummaries: true }));
            },
            onSuccess: (resp) => {
                console.log('jinro summaries search response', resp);
                setJinroSummariesRows(resp?.data ?? []);
                setLoadingState((prev) => ({ ...prev, jinroSummaries: false }));
            },
            onError: (error) => {
                console.log('jinro summaries error', error);
            },
            onFinal: () => {
                console.log('jinro summaries final');
            },
        });
    };

    useEffect(() => {
        callAPI({
            method: 'get',
            module: 'duty_calculator',
            component: 'jinro_summary',
            type: 'week_lists',
            getAccessTokenSilently,
            email: userEmail,
            onInit: () => {
                setLoadingState((prev) => ({ ...prev, weekList: true }));
            },
            onSuccess: (resp) => {
                const body = resp?.data && typeof resp.data === 'object' && !Array.isArray(resp.data) ? resp.data : {};
                const entry = Array.isArray(body.entryWeekList) ? body.entryWeekList : [];
                const trucking = Array.isArray(body.truckingWeekList) ? body.truckingWeekList : [];
                setEntryWeekList(entry);
                setTruckingWeekList(trucking);
                setLoadingState((prev) => ({ ...prev, weekList: false }));
                // 초기: Entry 기준 최신 주차 (smartsystem Jinro ERP 와 동일 개념)
                const latestEntryWeek = entry.length ? entry[entry.length - 1] : null;
                setWeek(latestEntryWeek);
                // 두 번째 API 호출 (entry 주차가 없으면 스킵)
                if (latestEntryWeek) {
                    callAPI({
                        method: 'get',
                        module: 'duty_calculator',
                        component: 'jinro_summaries',
                        type: 'entry',
                        condition: latestEntryWeek,
                        getAccessTokenSilently,
                        email: userEmail,
                        onInit: () => {
                            setLoadingState((prev) => ({ ...prev, jinroSummaries: true }));
                        },
                        onSuccess: (resp2) => {
                            console.log('jinro summaries response', resp2);
                            setJinroSummariesRows(resp2?.data ?? []);
                            setLoadingState((prev) => ({ ...prev, jinroSummaries: false }));
                        },
                        onError: (error2) => {
                            console.log('jinro summaries error', error2);
                        },
                        onFinal: () => {
                            console.log('jinro summaries final');
                        },
                    });
                } else {
                    setJinroSummariesRows([]);
                    setLoadingState((prev) => ({ ...prev, jinroSummaries: false }));
                }
            },
            onError: (error) => {
                console.log('jinro week list error', error);
            },
            onFinal: () => {
                console.log('jinro week list final');
            },
        });
    }, []);

    if (loadingState.weekList || loadingState.jinroSummaries) {
        return (
            <Box sx={{ p: 1 }}>
                <CustomSkeleton2 />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 1 }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SelectField_single_None
                        label={'TYPE'}
                        options={['ENTRY', 'TRUCKING']}
                        value={type}
                        onChange={handleTypeChange}
                        sx={{ gap: 1 }}
                        labelStyle={{ width: '35px', fontSize: '13px' }}
                        selectStyle={{ width: '110px' }}
                    />
                    <AutocompleteField_Single
                        label="WEEK"
                        name="week"
                        options={weekOptions ?? []}
                        value={week}
                        onChange={(event, newValue) => setWeek(newValue)}
                        getOptionLabel={(option) => (typeof option === 'string' ? option : (option ?? ''))}
                        isOptionEqualToValue={(a, b) => a === b}
                        disableClearable={true}
                        labelStyle={{ width: '35px', fontSize: '13px' }}
                        width="110px"
                        sx={{
                            gap: 1,
                            '& .MuiAutocomplete-root': {
                                height: '27px',
                            },
                            '& .MuiInputBase-input': {
                                fontSize: '13px',
                            },
                        }}
                    />
                    <Tooltip title="Search" placement="top" arrow>
                        <IconButton onClick={handleSearchClick} sx={{ color: 'white', p: 0.2, bgcolor: '#003a52' }}>
                            <SearchIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            <MainDatagrid
                rows={jinroSummariesRows}
                cols={columnsType === 'ENTRY' ? getEntryColumns() : getTruckingColumns()}
                showToolbar="CustomToolbar"
                id_status={false}
                additional={{
                    rowHeight: 40,
                    editMode: 'row',
                    disableColumnMenu: true,
                    cellSelection: true,
                    disableRowSelectionOnClick: true,
                    getDetailPanelContent: ({ row }) => (
                        <JinroERPDetail
                            row={row}
                            userEmail={userEmail}
                            getAccessTokenSilently={getAccessTokenSilently}
                            jinroSummariesRows={jinroSummariesRows}
                            setJinroSummariesRows={setJinroSummariesRows}
                            columnsType={columnsType}
                            setLoadingState={setLoadingState}
                        />
                    ),
                    getDetailPanelHeight: () => 'auto',
                }}
                sx={{
                    height: '80vh',
                    textAlign: 'center',
                    '& .MuiDataGrid-columnSeparator': { display: 'none' },
                    '& .MuiDataGrid-iconSeparator': { display: 'none' },
                }}
            />
        </Box>
    );
}
